import { Phone, MessageCircle, Refrigerator, Tv, Smartphone, Wind, CheckCircle, WashingMachine, Microwave, Monitor } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";

const ProductsPage = () => {
  const phoneNumber = "05075537051";
  const whatsappNumber = "905075537051";
  const whatsappMessage = encodeURIComponent("Merhaba, eşya satmak istiyorum. Bilgi alabilir miyim?");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const categories = [
    {
      title: "Beyaz Eşyalar",
      icon: Refrigerator,
      description: "Tüm marka ve model beyaz eşyalarınızı değerinde satın alıyoruz.",
      products: [
        { name: "Buzdolabı", details: "Tek kapı, çift kapı, no-frost, mini buzdolabı" },
        { name: "Çamaşır Makinesi", details: "Önden yüklemeli, üstten yüklemeli, tüm kapasiteler" },
        { name: "Bulaşık Makinesi", details: "Ankastre ve solo modeller, tüm boyutlar" },
        { name: "Kurutma Makinesi", details: "Kondensasyonlu ve ısı pompalı modeller" },
        { name: "Ankastre Set", details: "Fırın, ocak, davlumbaz, mikrodalga" },
        { name: "Derin Dondurucu", details: "Sandık tipi ve dik modeller" },
      ],
    },
    {
      title: "Televizyonlar",
      icon: Tv,
      description: "LED TV, Smart TV, QLED TV ve tüm televizyon türlerini alıyoruz.",
      products: [
        { name: "LED TV", details: "32 inç'ten 85 inç'e tüm boyutlar" },
        { name: "LCD TV", details: "Tüm marka ve modeller" },
        { name: "Smart TV", details: "Wi-Fi özellikli akıllı televizyonlar" },
        { name: "QLED TV", details: "Samsung ve diğer markalar" },
        { name: "Android TV", details: "Google TV, Android işletim sistemli" },
        { name: "OLED TV", details: "LG ve Sony OLED modeller" },
      ],
    },
    {
      title: "Klimalar",
      icon: Wind,
      description: "Split klima ve salon tipi klimalarınızı satın alıyoruz.",
      products: [
        { name: "Split Klima", details: "9000 BTU'dan 24000 BTU'ya" },
        { name: "Inverter Klima", details: "Enerji tasarruflu modeller" },
        { name: "Salon Tipi Klima", details: "Büyük alan klimaları" },
        { name: "Multi Split Klima", details: "Çoklu iç ünite sistemleri" },
      ],
    },
    {
      title: "Ev Elektroniği",
      icon: Smartphone,
      description: "Akıllı cihazlar ve ev elektroniği ürünlerini değerlendiriyoruz.",
      products: [
        { name: "Ses Sistemleri", details: "Hoparlör, soundbar, ev sinema" },
        { name: "Oyun Konsolları", details: "PlayStation, Xbox, Nintendo" },
        { name: "Elektrikli Süpürge", details: "Robot ve diğer modeller" },
        { name: "Akıllı Ev Cihazları", details: "Alexa, Google Home ve benzeri" },
        { name: "Küçük Ev Aletleri", details: "Kahve makinesi, ekmek kızartma vb." },
      ],
    },
  ];

  const conditions = [
    {
      title: "Sıfır Eşya",
      description: "Kutusunda hiç açılmamış veya çok az kullanılmış sıfır ayarında eşyalar",
      color: "bg-green-50 border-green-200 text-green-700",
    },
    {
      title: "İkinci El Eşya",
      description: "Kullanılmış ancak çalışır durumda olan tüm eşyalar",
      color: "bg-blue-50 border-blue-200 text-blue-700",
    },
    {
      title: "Defolu Eşya",
      description: "Kozmetik hasarlı, çizik veya küçük arızalı eşyalar",
      color: "bg-orange-50 border-orange-200 text-orange-700",
    },
    {
      title: "Arızalı Eşya",
      description: "Çalışmayan veya onarım gerektiren eşyalar (değerlendirmeye alınır)",
      color: "bg-red-50 border-red-200 text-red-700",
    },
  ];

  return (
    <div data-testid="products-page">
      {/* Hero Section */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#002FA7] font-semibold text-sm uppercase tracking-widest mb-3">
              SATIN ALDIĞIMIZ EŞYALAR
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Hangi Eşyaları Satın Alıyoruz?
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Beyaz eşyadan televizyona, klimadan ev elektroniğine kadar geniş bir ürün yelpazesinde alım yapıyoruz. Sıfır, ikinci el veya defolu tüm eşyalarınız için nakit ödeme yapıyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Conditions Section */}
      <section className="py-12 border-b border-slate-200" data-testid="conditions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-slate-900 mb-6 text-center">Her Durumda Alıyoruz</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {conditions.map((condition, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${condition.color}`}
                data-testid={`condition-${index}`}
              >
                <h3 className="font-semibold mb-1">{condition.title}</h3>
                <p className="text-sm opacity-80">{condition.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {categories.map((category, index) => (
              <Card key={index} className="overflow-hidden border border-slate-200" data-testid={`category-${index}`}>
                <CardContent className="p-0">
                  <div className="bg-slate-50 p-6 border-b border-slate-200">
                    <div className="flex items-center gap-4">
                      <div className="service-icon">
                        <category.icon className="w-7 h-7" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900">{category.title}</h2>
                        <p className="text-slate-600">{category.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.products.map((product, i) => (
                        <div
                          key={i}
                          className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-[#002FA7] transition-colors"
                          data-testid={`product-${index}-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-[#128C7E] flex-shrink-0 mt-0.5" />
                            <div>
                              <h3 className="font-semibold text-slate-900">{product.name}</h3>
                              <p className="text-sm text-slate-500">{product.details}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-8 text-center">
            Tüm Markalar Değerlendirilir
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              "Samsung", "LG", "Sony", "Arçelik", "Beko", "Vestel", "Philips",
              "Bosch", "Siemens", "Profilo", "Toshiba", "Panasonic", "TCL",
              "Hisense", "Sharp", "Grundig", "Altus", "Regal", "SEG"
            ].map((brand) => (
              <span
                key={brand}
                className="px-4 py-2 bg-white rounded-lg border border-slate-200 text-slate-700 font-medium text-sm"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24" data-testid="how-it-works">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-8 text-center">
            Nasıl Satarsınız?
          </h2>
          <div className="space-y-4">
            {[
              { step: "1", title: "Fotoğraf Çekin", desc: "Eşyanızın fotoğraflarını çekin. Marka, model ve genel durumunu gösteren açık fotoğraflar çekin." },
              { step: "2", title: "WhatsApp'tan Gönderin", desc: "0507 553 70 51 numaralı WhatsApp hattımıza fotoğrafları gönderin ve kısa bir açıklama ekleyin." },
              { step: "3", title: "Teklif Alın", desc: "Uzman ekibimiz fotoğrafları inceleyip size en uygun fiyat teklifini sunar." },
              { step: "4", title: "Nakit Alın", desc: "Teklifi kabul ederseniz, aynı gün veya size uygun bir zamanda adresinize gelir ve nakit ödeme yaparız." },
            ].map((item, index) => (
              <div key={index} className="flex gap-4 p-4 bg-slate-50 rounded-lg" data-testid={`how-step-${index}`}>
                <div className="step-number flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#002FA7]" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Eşyanızı Hemen Nakite Çevirin
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Fotoğraf gönderin, anında teklif alın. İstanbul genelinde aynı gün hizmet!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${phoneNumber}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#002FA7] px-6 py-3.5 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
              data-testid="products-cta-phone-btn"
            >
              <Phone className="w-5 h-5" />
              <span>0507 553 70 51</span>
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-lg font-semibold hover:bg-[#128C7E] transition-colors"
              data-testid="products-cta-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp'tan Yaz</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
